import type { Item } from "@/types/api";

export const NEXT_EPISODE_PROMPT_THRESHOLD_SECONDS = 30;
export const NEXT_EPISODE_AUTOPLAY_DELAY_SECONDS = 10;

type NextEpisodePromptState = {
  currentTime: number;
  duration: number;
  playbackEnded: boolean;
  hasNextEpisode: boolean;
  playerError: string | null;
  seekSwitching: boolean;
  previewSeeking: boolean;
  qualitySwitching: boolean;
  pendingSeekTime: number | null;
  recoveringSession: boolean;
};

function compareEpisodeOrder(left: Item, right: Item): number {
  const leftSeason = left.attributes.season_number ?? Number.MAX_SAFE_INTEGER;
  const rightSeason = right.attributes.season_number ?? Number.MAX_SAFE_INTEGER;
  if (leftSeason !== rightSeason) {
    return leftSeason - rightSeason;
  }

  const leftEpisode = left.attributes.episode_number ?? Number.MAX_SAFE_INTEGER;
  const rightEpisode = right.attributes.episode_number ?? Number.MAX_SAFE_INTEGER;
  if (leftEpisode !== rightEpisode) {
    return leftEpisode - rightEpisode;
  }

  const leftRelease = left.attributes.release_date ?? "";
  const rightRelease = right.attributes.release_date ?? "";
  if (leftRelease !== rightRelease) {
    return leftRelease.localeCompare(rightRelease);
  }

  const leftCreated = left.attributes.created_at ?? "";
  const rightCreated = right.attributes.created_at ?? "";
  if (leftCreated !== rightCreated) {
    return leftCreated.localeCompare(rightCreated);
  }

  const leftTitle = (left.attributes.episode_title ?? left.attributes.title).toLowerCase();
  const rightTitle = (right.attributes.episode_title ?? right.attributes.title).toLowerCase();
  if (leftTitle !== rightTitle) {
    return leftTitle.localeCompare(rightTitle);
  }

  return left.id.localeCompare(right.id);
}

export function sortSeriesEpisodes(items: Item[]): Item[] {
  return [...items]
    .filter((item) => item.attributes.item_type === "episode")
    .sort(compareEpisodeOrder);
}

export function findNextEpisode(currentItem: Item | null | undefined, candidateItems: Item[]): Item | null {
  if (!currentItem || currentItem.attributes.item_type !== "episode") {
    return null;
  }

  const currentSeriesID = currentItem.attributes.series_id ?? null;
  const currentSeriesTitle = currentItem.attributes.series_title ?? null;
  const orderedEpisodes = sortSeriesEpisodes(candidateItems).filter((item) => {
    if (item.attributes.item_type !== "episode") {
      return false;
    }
    if (currentSeriesID && item.attributes.series_id) {
      return item.attributes.series_id === currentSeriesID;
    }
    if (currentSeriesTitle && item.attributes.series_title) {
      return item.attributes.series_title === currentSeriesTitle;
    }
    return true;
  });

  const currentIndex = orderedEpisodes.findIndex((item) => item.id === currentItem.id);
  if (currentIndex < 0 || currentIndex >= orderedEpisodes.length - 1) {
    return null;
  }
  return orderedEpisodes[currentIndex + 1] ?? null;
}

export function shouldShowNextEpisodePrompt(state: NextEpisodePromptState): boolean {
  if (!state.hasNextEpisode || !state.duration || state.duration <= 0) {
    return false;
  }
  if (state.playerError || state.seekSwitching || state.previewSeeking || state.qualitySwitching) {
    return false;
  }
  if (state.pendingSeekTime !== null || state.recoveringSession) {
    return false;
  }
  if (state.playbackEnded) {
    return true;
  }
  const remainingSeconds = Math.max(0, state.duration - state.currentTime);
  return remainingSeconds <= NEXT_EPISODE_PROMPT_THRESHOLD_SECONDS;
}
